/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { SidebarDropdown } from "~/components/SidebarForm/inputs/SidebarDropdown";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { showSuccess } from "~/components/Toast";
import { Employee } from "~/models";
import { useAxios } from "~/tools";

type Props = {
  employee: Employee;
  refetch: () => Promise<void>;
};

enum PermissionRoles {
  BASIC = "BASIC",
  STANDARD = "STANDARD",
  PRIVILEGED = "PRIVILEGED",
  SUPER_ADMIN = "SUPER_ADMIN",
}

export const availableRoles = Object.keys(PermissionRoles).map((el) => ({
  label: el.replace("_", " "),
  value: el,
}));

function EmployeesRoleSidebar({ employee, refetch }: Props) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const methods = useForm();
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const onSubmit = async (values: Employee) => {
    const data = { permission: values.permission };
    try {
      await Axios().put(`/employees/${employee.id}/permission`, data);
      setIsSidebarOpen(false);
      refetch();
      showSuccess("Changes will be reflected in the system after 5 minutes");
    } catch (e) {
      showError(e.response);
    }
  };

  return (
    <>
      <Button
        icon="settings"
        size="sm"
        onClick={() => setIsSidebarOpen(true)}
        severity="low"
      >
        Edit Role
      </Button>

      <SidebarForm
        title="Edit Role"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onSubmit}
      >
        <SidebarDropdown
          name="permission"
          label="Role"
          options={availableRoles}
          defaultValue={employee.permission}
        />
        <SidebarFormButtons onCancel={() => setIsSidebarOpen(false)} />
      </SidebarForm>
    </>
  );
}

export { EmployeesRoleSidebar };
